import type {
  Assignee,
  ConversationListOptions,
  ConversationListPage,
  ConversationSort,
  EventSummary,
  RequestListItem,
} from "../api/types";
// Leaf import on purpose: this module is pure logic and must stay loadable without a DOM,
// which the `@/lib` barrel is not (it pulls in the design system).
import { fmtFactDate } from "@/lib/fact-date";

export type InboxStatusBucket = "new" | "in_progress" | "new_message" | "answered" | "done";
export type InboxAssignmentFilter = "all" | "review" | "unassigned";
export type InboxTaskFilter = "new" | "review" | "waiting" | "all" | "archived" | "trash";
export type InboxActiveSection = Exclude<InboxTaskFilter, "all" | "archived" | "trash">;
export type InboxPeriodPreset = "all" | "today" | "week" | "month" | "custom";

export const INBOX_ACTIVE_SECTIONS: InboxActiveSection[] = ["new", "review", "waiting"];
export const INBOX_PERIOD_PRESETS: InboxPeriodPreset[] = ["all", "today", "week", "month", "custom"];

/** Completed requests belong to none of the live queues during their undo grace period. */
export function isGroupedInboxView(task: InboxTaskFilter, status: InboxStatusBucket | ""): boolean {
  return task === "all" && status !== "done";
}

/**
 * #814: the sort names the console offers, in the order they appear in the control. Both are
 * server orders — the browser never re-sorts a page it was given, because it only ever holds one
 * page of a longer list and sorting that would put the wrong conversations on it.
 */
export const INBOX_SORTS: ConversationSort[] = ["activity_desc", "activity_asc"];
export const INBOX_DEFAULT_SORT: ConversationSort = "activity_desc";

/**
 * The period control's own state (#814): two ends, either of which may be empty.
 */
export interface InboxDateRange {
  from: string;
  to: string;
}

export const EMPTY_INBOX_DATE_RANGE: InboxDateRange = { from: "", to: "" };

const TENANT_TIME_ZONE = "Europe/Berlin";

function tenantCalendarDate(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TENANT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function shiftCalendarDate(value: string, days: number): string {
  const [year, month, day] = value.split("-").map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1, day! + days));
  return shifted.toISOString().slice(0, 10);
}

/**
 * Named periods are resolved in the same fixed tenant calendar as the server. Returning the
 * existing open-ended range for `custom` keeps the former two-field semantics intact.
 */
export function inboxPeriodDateRange(
  preset: InboxPeriodPreset,
  now: Date,
  customRange: InboxDateRange = EMPTY_INBOX_DATE_RANGE,
): InboxDateRange {
  if (preset === "all") return EMPTY_INBOX_DATE_RANGE;
  if (preset === "custom") return customRange;

  const today = tenantCalendarDate(now);
  if (preset === "today") return { from: today, to: today };

  const [year, month, day] = today.split("-").map(Number);
  if (preset === "month") {
    const from = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
    const to = new Date(Date.UTC(year!, month!, 0)).toISOString().slice(0, 10);
    return { from, to };
  }

  const weekday = new Date(Date.UTC(year!, month! - 1, day!)).getUTCDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  const from = shiftCalendarDate(today, -daysFromMonday);
  return { from, to: shiftCalendarDate(from, 6) };
}

/** Mirrors of the server's limits. The server remains the authority and answers 400 on its own;
 * these only keep the console from sending a request it already knows will be refused, and let it
 * say why in the operator's language instead of showing a failed load. */
const INBOX_DATE_MIN = "2000-01-01";
const INBOX_DATE_MAX = "2100-12-31";
const INBOX_DATE_RANGE_MAX_DAYS = 366;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type InboxDateRangeProblem = "reversed" | "tooWide" | "outOfCalendar";

/**
 * What is wrong with the period, or nothing.
 *
 * An empty end is not a problem — it is an open period, which the server accepts. A half-typed
 * year is: a native date input reports `0202-08-01` as a perfectly good date, and a filter that
 * quietly matched nothing (or everything) would read as a list that lost its rows.
 */
export function inboxDateRangeProblem(range: InboxDateRange): InboxDateRangeProblem | null {
  const ends = [range.from, range.to].filter((value) => value !== "");
  if (ends.some((value) => value < INBOX_DATE_MIN || value > INBOX_DATE_MAX)) return "outOfCalendar";
  if (!range.from || !range.to) return null;
  if (range.to < range.from) return "reversed";
  const span = Date.parse(`${range.to}T00:00:00.000Z`) - Date.parse(`${range.from}T00:00:00.000Z`);
  return Math.round(span / MILLISECONDS_PER_DAY) + 1 > INBOX_DATE_RANGE_MAX_DAYS ? "tooWide" : null;
}

export function isInboxDateRangeSet(range: InboxDateRange): boolean {
  return range.from !== "" || range.to !== "";
}

/** The period as the server takes it. Only a usable period is ever sent. */
export function conversationDateQuery(
  range: InboxDateRange,
): Pick<ConversationListOptions, "date_from" | "date_to"> {
  if (inboxDateRangeProblem(range)) return {};
  return {
    ...(range.from ? { date_from: range.from } : {}),
    ...(range.to ? { date_to: range.to } : {}),
  };
}

export interface InboxTaskCounts {
  new: number;
  review: number;
  waiting: number;
  all: number;
  archived: number;
  trash: number;
}

/**
 * The operator tasks are presentation shortcuts over the existing server projection.
 * No lifecycle or business state is inferred in the browser.
 *
 * Archived is the one that is not a filter (#813): it asks the server for the other scope, where
 * "all" means all of the archive rather than all of the worklist. The trash (#1787) is a scope of
 * its own in the same way.
 */
export function conversationQueryForTask(
  task: InboxTaskFilter,
  assignment: InboxAssignmentFilter,
  lifecycleStatus: InboxStatusBucket | "" = "",
): Pick<ConversationListOptions, "status" | "assignment" | "scope" | "queue"> | null {
  if (task === "archived") return { status: lifecycleStatus, assignment, scope: "archived" };
  if (task === "trash") return { status: lifecycleStatus, assignment, scope: "trash" };
  if (task === "review") {
    return {
      status: lifecycleStatus,
      queue: "review",
      assignment: assignment === "unassigned" ? "review_unassigned" : "review",
      scope: "active",
    };
  }
  const taskStatus = task === "waiting" ? "answered" : "";
  // Waiting keeps its existing lifecycle category; Neu is independent opening state.
  if (taskStatus && lifecycleStatus && lifecycleStatus !== taskStatus) return null;
  return {
    status: lifecycleStatus || taskStatus,
    ...(task === "new" ? { queue: "new" as const } : {}),
    assignment,
    scope: "active",
  };
}

/**
 * One unfiltered server count projection per scope supplies every task badge. The archive and the
 * trash are counted by the server that lists them, so the badge and the list can never disagree.
 */
export function inboxTaskCounts(
  page: ConversationListPage,
  archived?: ConversationListPage | null,
  trash?: ConversationListPage | null,
): InboxTaskCounts {
  return {
    new: page.counts.unopened ?? 0,
    review: page.counts.attention,
    waiting: page.counts.by_status.answered ?? 0,
    all: page.counts.total,
    archived: archived?.counts.total ?? 0,
    trash: trash?.counts.total ?? 0,
  };
}

/**
 * A grouped card is only a server-paged preview. `next_offset` decides whether a continuation
 * exists; `total`, `offset`, and the rows actually returned decide the truthful remaining count.
 */
export function groupedPreviewRemainingCount(page: ConversationListPage | null): number {
  if (!page || page.next_offset === null) return 0;
  return Math.max(0, page.total - page.offset - page.items.length);
}

export const INBOX_STATUS_BUCKETS: InboxStatusBucket[] = [
  "new",
  "in_progress",
  "new_message",
  "answered",
  "done",
];

/** The server sorts before pagination; preserving rendered order would mislabel a live page. */
export function acceptServerConversationPage(
  _current: ConversationListPage | null,
  incoming: ConversationListPage,
): ConversationListPage {
  return incoming;
}

/** A customer reply has arrived on this request and the operator hasn't opened it yet. */
export function showNewReplyBadge(
  row: Pick<RequestListItem, "has_unhandled_reply">,
): boolean {
  return row.has_unhandled_reply === true;
}

export function requestStatusBucket(
  status: string | null | undefined,
): InboxStatusBucket {
  return INBOX_STATUS_BUCKETS.includes(status as InboxStatusBucket)
    ? status as InboxStatusBucket
    : "new";
}

/** Every request belongs to exactly one operator lifecycle lane. */
export function boardLane(row: Pick<RequestListItem, "lifecycle_status">): InboxStatusBucket {
  return requestStatusBucket(row.lifecycle_status);
}

/** 1..5 visual position for the lifecycle indicator. */
export function stageStep(row: Pick<RequestListItem, "lifecycle_status">): number {
  const bucket = requestStatusBucket(row.lifecycle_status);
  if (bucket === "new") return 1;
  if (bucket === "in_progress") return 2;
  if (bucket === "new_message") return 3;
  if (bucket === "answered") return 4;
  return 5;
}

/** New persists until work starts; unread customer mail highlights every lifecycle lane. */
export function shouldHighlightRequest(
  row: Pick<RequestListItem, "lifecycle_status" | "has_unhandled_reply">,
): boolean {
  return row.lifecycle_status === "new"
    || row.has_unhandled_reply === true;
}

/** What `fmtFactDate` prints when it cannot read a value. */
const FACT_DATE_UNKNOWN = "—";

/**
 * The event context a worklist row shows next to the customer (#815): occasion, date, city — in
 * that order, with whatever the server could not resolve simply absent.
 *
 * Nothing is decided here. The server already refused every value it had not accepted, so an
 * empty result means the request genuinely has no event facts yet, and the row should say that
 * rather than leave a gap the operator has to interpret.
 */
export function eventSummaryParts(summary: EventSummary | null | undefined): string[] {
  if (!summary) return [];
  const parts = [
    summary.occasion,
    calendarDate(summary.event_start_date),
    summary.venue_city,
  ];
  return parts.filter((part): part is string => typeof part === "string" && part.length > 0);
}

/**
 * `fmtFactDate` reports "unknown" as an em dash, which is right in a labelled field and wrong
 * here: between two real values it would read as a third one. Absent is the honest answer.
 */
function calendarDate(iso: string | null): string | null {
  if (!iso) return null;
  const formatted = fmtFactDate(iso);
  return formatted === FACT_DATE_UNKNOWN ? null : formatted;
}

/**
 * Display name for one principal on a request (#803): the person's name, else their email; for a
 * team, its name. Null only when there is nothing to name.
 */
export function assigneeName(principal: Assignee | null | undefined): string | null {
  if (!principal) return null;
  if (principal.kind === "team") return principal.name;
  const name = (principal.name ?? "").trim();
  return name || principal.email;
}

/**
 * What the inbox column shows for a whole set. The column is one line wide, so it names the first
 * few and counts the rest rather than wrapping — `extra` is what is not shown, never a total.
 */
export function assigneeSummary(
  assignees: readonly Assignee[],
  limit = 2,
): { labels: string[]; extra: number } {
  const named = assignees
    .map((principal) => assigneeName(principal))
    .filter((label): label is string => Boolean(label));
  return { labels: named.slice(0, limit), extra: Math.max(0, named.length - limit) };
}
